import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { User } from '@/types/common.types';

interface AuthState {
  token: string | null;
  user: User | null;
  tenantId: string | null;
  isAuthenticated: boolean;
  login: (token: string, user: User, tenantId: string) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
}

/**
 * Persists JWT and user to `localStorage` via zustand persist. Mitigate XSS risk in deployment
 * (CSP, dependency hygiene); httpOnly cookies would require a different auth model.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      tenantId: null,
      isAuthenticated: false,
      login: (token, user, tenantId) => {
        set({ token, user, tenantId: tenantId.toLowerCase(), isAuthenticated: true });
      },
      logout: () => {
        set({ token: null, user: null, tenantId: null, isAuthenticated: false });
      },
      updateUser: (userUpdates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userUpdates } : null,
        }));
      },
    }),
    {
      name: 'supplier-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        tenantId: state.tenantId,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
